// The owner's view of the agents (the owner-agents prototype, "AI agents"): the company kill switch,
// this month's spend against the cap, each agent with its switch, what it did today (automatic,
// asked, refused), its budget and what waits for a person, then the day's activity with each run's
// steps, and the recent runs (RecentRuns.tsx). Switches, budgets and a run's cost are the owner's
// (agents.manage); the manager reads.
// BL-189: on DashboardPage, one column of Sections: the kill switch (what it does, the owner's danger
// Button under it), the month's spend (Figures and a Meter), the agents (each a RowList row: its budget a
// line, On or Stopped at its end, Turn off beside it), then Activity today and Recent runs.

import { useState, type ReactNode } from 'react';
import type { Approval } from '../books/types.ts';
import { RpcError } from '@flowology/client';
import {
  Button,
  Figures,
  Label,
  Link,
  Meter,
  RowList,
  Segmented,
  SelectField,
  TextField,
  type Row,
} from '@flowology/ui-web/fl';
import { DashboardPage, type PageHeadProps } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { money } from '../books/model.ts';
import { errorText, useAct, useQuery } from '../books/ui.tsx';
import { agentKeyOf } from '../shared/ApprovalsInbox.tsx';
import {
  stepChip,
  actionWords,
  activityInput,
  budgetLevel,
  clock,
  roleLabel,
  taskText,
  type Mode,
} from './model.ts';
import type { AgentsProps } from './props.ts';
import { RecentRuns } from './RecentRuns.tsx';
import { RunPanel } from './RunPanel.tsx';
import { PartSays, agentOptions, toneOf } from './agents-parts.tsx';
import { type ActivityPage, type ActivityStep, type AgentSummary, type AgentsList } from './types.ts';
import { usePaged } from '../blueprint/paged.ts';
import { shownText } from '../shared/pages.ts';

/** A budget's bar: red at its cap; none drawn for a company with no cap. */
const meterOf = (share: number, level: string, label: string) => ({
  value: Math.round(share * 100),
  label,
  state: level === 'bad' ? ('bad' as const) : ('ok' as const),
});

/** One agent, a row: On (or Stopped, why on a line of its own) and Turn off beside it, its budget a line. */
function agentRow(
  a: AgentSummary,
  o: {
    waiting: number;
    props: AgentsProps;
    companyStopped: boolean;
    busy: boolean;
    onSwitch: (a: AgentSummary) => void;
  },
): Row {
  const b = budgetLevel(a.spentCents, a.budgetCents);
  const says: ReactNode = (
    <span className="agents-says">
      <Label kind="now">{a.today.automatic} automatic</Label>
      <Label kind="wait">{a.today.asked} asked</Label>
      {a.today.refused > 0 && <Label kind="bad">{a.today.refused} refused</Label>}
      {o.waiting > 0 && <Link onPress={() => o.props.go('approvals')}>{`${o.waiting} waiting →`}</Link>}
    </span>
  );
  // the budget a line; near its cap the share used beside it (the spend's Meter is the company's)
  const budget: ReactNode = (
    <>
      {`Budget this month ${b.text} · ${a.runsThisMonth} ${a.runsThisMonth === 1 ? 'run' : 'runs'} this month`}
      {b.level === 'warn' && (
        <>
          {' '}
          <Label kind="wait">{Math.round(b.share * 100)} % used</Label>
        </>
      )}
    </>
  );
  const switchedOn = !a.switchedOff;
  return {
    id: a.key,
    name: a.name,
    off: !a.on,
    lines: [
      { text: `Role: ${roleLabel(a.role)} · ${a.runs}` },
      ...(a.on
        ? []
        : [
            {
              word: `${a.stoppedReason?.replace(/\.$/, '') ?? 'Stopped'}${a.key === 'receptionist' ? ' · calls ring the office' : ''}`,
              tone: 'bad' as const,
            },
          ]),
      { part: says },
      { text: budget },
    ],
    end: a.on ? { word: 'On' } : { word: 'Stopped', tone: 'bad' },
    atRest: 2,
    actions: [
      {
        id: 'switch',
        text: switchedOn ? 'Turn off' : 'Turn on',
        label: switchedOn ? `Turn ${a.name} off` : `Turn ${a.name} on`,
        onPress: () => o.onSwitch(a),
        isDisabled: !o.props.isOwner || o.companyStopped || o.busy,
      },
      {
        id: 'settings',
        text: 'Settings',
        label: `${a.name}: settings`,
        onPress: () => o.props.go('settings', a.key),
      },
    ],
  };
}

const FILTERS: { id: Mode | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'automatic', label: 'Automatic' },
  { id: 'asked', label: 'Asked' },
  { id: 'refused', label: 'Refused' },
];

/** One step the agents took today, a row: the agent, what it did, how it went; Open run shows its run. */
function stepRow(
  s: ActivityStep,
  o: {
    names: Map<string, string>;
    props: AgentsProps;
    open: string | null;
    onOpen: (id: string | null) => void;
  },
): Row {
  const id = `${s.runId}:${s.n}`;
  const [tone, word] = stepChip(s);
  const name = o.names.get(s.agentKey) ?? s.agentKey;
  const shown = o.open === id;
  return {
    id,
    name,
    kicker: clock(s.at, o.props.timeZone),
    lines: [
      {
        // the action in words, its dotted name on hover (F-04)
        text: (
          <>
            <span title={s.action ?? undefined}>{actionWords(s.action, s.actionTitle)}</span> ·{' '}
            {taskText(s.task)}
          </>
        ),
        tone: 'ink',
        size: 'sm',
      },
      ...(s.decision?.note ? [{ text: `Note to the agent: “${s.decision.note}”` }] : []),
      ...(shown
        ? [{ part: <RunPanel runId={s.runId} timeZone={o.props.timeZone} showCost={o.props.isOwner} /> }]
        : []),
    ],
    end: { word, tone: toneOf(tone) },
    actions: [
      {
        id: 'run',
        text: shown ? 'Hide run' : 'Open run',
        label: `${shown ? 'Hide' : 'Open'} run: ${name}, ${clock(s.at, o.props.timeZone)}`,
        onPress: () => o.onOpen(shown ? null : id),
      },
    ],
  };
}

function Activity({ props, names }: { props: AgentsProps; names: Map<string, string> }) {
  const [mode, setMode] = useState<Mode | ''>('');
  const [agentKey, setAgentKey] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  // the mode and agent filtered by the server, a page at a time (list caps M16): a busy day passes a page
  const day = usePaged<ActivityStep, ActivityPage>(
    'agents.activity',
    activityInput(agentKey, mode),
    (p) => p.steps,
  );
  const rows = day.rows ?? [];
  const total = day.first?.total ?? rows.length;
  return (
    <>
      <div className="agents-tools">
        <Segmented
          label="Filter activity"
          items={FILTERS}
          selectedKey={mode || 'all'}
          onSelectionChange={(k) => setMode(k === 'all' ? '' : (k as Mode))}
        />
        <SelectField
          label="Agent"
          hideLabel
          width="fit"
          value={agentKey}
          onChange={setAgentKey}
          options={agentOptions(names)}
        />
      </div>
      <PartSays
        status={day.rows ? 'ready' : day.error ? 'failed' : 'loading'}
        what="today’s activity"
        failedText={day.error ?? undefined}
        onRetry={day.reload}
      >
        <RowList
          label="Activity today"
          rows={rows.map((s) => stepRow(s, { names, props, open, onOpen: setOpen }))}
          empty="Nothing yet today."
          {...(day.more ? { onMore: () => day.more?.() } : {})}
          {...(day.more || day.busy ? { note: shownText(rows.length, total) } : {})}
        />
      </PartSays>
    </>
  );
}

/** A step-up on its own (/me/2fa/step-up): the next ten minutes count as a fresh second factor. */
async function stepUpWith(code: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const csrf = (await client.session())?.csrf;
  const res = await fetch('/me/2fa/step-up', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(csrf ? { 'x-flow-csrf': csrf } : {}) },
    body: JSON.stringify({ code: code.trim() }),
  });
  const body = (await res.json().catch(() => null)) as { ok?: boolean; error?: { message: string } } | null;
  return res.ok && body?.ok
    ? { ok: true }
    : { ok: false, message: body?.error?.message ?? 'That code did not work.' };
}

export function Overview(props: AgentsProps & { head: PageHeadProps; said: ReactNode }) {
  const list = useQuery<AgentsList>('agents.list', {});
  const pending = useQuery<{ approvals: Approval[] }>('approvals.list', { status: 'pending' });
  const [armed, setArmed] = useState(false);
  // Restarting after the kill switch asks for a fresh two-factor code first (QA-099).
  const [stepCode, setStepCode] = useState<string | null>(null);
  // An owner with no two-factor of their own turns it on before restarting (QA-300): where to.
  const [ownOff, setOwnOff] = useState<string | null>(null);
  const act = useAct(props.toast, async () => {
    await list.reload();
  });
  const month = new Intl.DateTimeFormat('en-CA', { month: 'long' }).format(new Date());
  const agents = list.data?.agents ?? [];
  const company = list.data?.company;
  const names = new Map(agents.map((a) => [a.key, a.name]));
  const waitingFor = (key: string) =>
    pending.data?.approvals.filter((a) => agentKeyOf(a.agent) === key).length ?? 0;

  const stopAll = async () => {
    if (!company) return;
    if (!company.stopped && !armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    if (company.stopped) {
      // The owner's fresh second factor: straight on if they passed one in the last 10 minutes.
      try {
        await client.call('agents.setStopped', { agentKey: '*', stopped: false });
        props.toast('Agents are back on.');
        await list.reload();
      } catch (e) {
        const detail = (e as { detail?: { reason?: string; setUp?: string } }).detail;
        if (e instanceof RpcError && detail?.reason === 'step_up_required') setStepCode('');
        else if (e instanceof RpcError && detail?.reason === 'own_two_factor_off')
          setOwnOff(detail.setUp ?? '/');
        else props.toast(errorText(e));
      }
      return;
    }
    await act.run('agents.setStopped', { agentKey: '*', stopped: true }, 'Every agent is stopped.');
  };
  const confirmRestart = async () => {
    const res = await stepUpWith(stepCode ?? '');
    if (!res.ok) return props.toast(res.message);
    setStepCode(null);
    await act.run('agents.setStopped', { agentKey: '*', stopped: false }, 'Agents are back on.');
  };
  const flip = (a: AgentSummary) => {
    const turningOff = !a.switchedOff;
    void act.run(
      'agents.setStopped',
      { agentKey: a.key, stopped: turningOff },
      turningOff ? `${a.name} is off.` : `${a.name} is back on.`,
    );
  };

  // the kill switch: what it does, then the owner's danger Button (and Cancel once it asks again)
  const kill = (stopped: boolean) => (
    <>
      <p className="agents-line">
        {stopped && <b>All agents are stopped. </b>}
        {stopped
          ? 'No agent can call a model or a tool, and no outside AI tool (connected with someone’s sign-in) can act. The phone rings the office line, then voicemail.'
          : 'Stops every agent before its next model or tool call, and outside AI tools connected with someone’s sign-in too. Requests waiting for approval can’t run until you turn them back on.'}
      </p>
      {!props.isOwner && (
        <p className="agents-line" data-muted="">
          Only the owner stops the agents.
        </p>
      )}
      {props.isOwner && (
        <p className="agents-kill-acts">
          <Button variant="danger" isDisabled={act.busy} onPress={() => void stopAll()}>
            {stopped ? 'Turn agents back on' : armed ? 'Tap again to stop all' : 'Stop all agents'}
          </Button>
          {armed && <Link onPress={() => setArmed(false)}>Cancel</Link>}
        </p>
      )}
      {ownOff !== null && (
        <p className="agents-line" role="status">
          Turn on your own two-factor first, then come back to do this. <Link href={ownOff}>Set it up</Link>
        </p>
      )}
      {stepCode !== null && (
        <form
          className="agents-stepup"
          onSubmit={(e) => {
            e.preventDefault();
            void confirmRestart();
          }}
        >
          <TextField
            label="Code from your authenticator app"
            inputMode="numeric"
            value={stepCode}
            onChange={setStepCode}
          />
          <Button type="submit" variant="primary" isDisabled={act.busy || !stepCode.trim()}>
            Confirm and turn back on
          </Button>
        </form>
      )}
    </>
  );
  // this month's spend against the company's cap
  const spend = (c: AgentsList['company']) => {
    const cap = budgetLevel(c.spentCents, c.budgetCents);
    return (
      <div className="agents-spend">
        <Figures
          size="sm"
          label={`${month} spend`}
          items={[
            { label: 'Spent this month', value: money(c.spentCents) },
            {
              label: 'Monthly cap',
              value: c.budgetCents === null ? 'None' : money(c.budgetCents),
              ...(c.budgetCents === null ? { sub: 'Set one in Settings' } : {}),
              bad: cap.level === 'bad',
            },
          ]}
        />
        {c.budgetCents !== null && (
          <Meter {...meterOf(cap.share, cap.level, `${month} spend against the cap`)} />
        )}
        <p className="agents-line" data-muted="">
          At 100 % of a budget the agent stops before its next model call (the receptionist leaves calls to
          the office line and voicemail).
        </p>
      </div>
    );
  };

  return (
    <DashboardPage
      head={props.head}
      notice={props.said}
      waiting={{
        status: list.data ? 'ready' : list.error ? 'failed' : 'loading',
        what: 'the agents',
        failedText: list.error ?? 'The agents didn’t load.',
        onRetry: () => void list.reload(),
      }}
      main={
        company
          ? [
              {
                title: 'Company kill switch',
                children: <div className="agents-kill">{kill(company.stopped)}</div>,
              },
              { title: `${month} spend`, children: spend(company) },
              {
                title: 'Agents',
                count: agents.length,
                children: (
                  <RowList
                    label="Agents"
                    rows={agents.map((a) =>
                      agentRow(a, {
                        waiting: waitingFor(a.key),
                        props,
                        companyStopped: company.stopped,
                        busy: act.busy,
                        onSwitch: flip,
                      }),
                    )}
                  />
                ),
              },
              { title: 'Activity today', children: <Activity props={props} names={names} /> },
              { title: 'Recent runs', children: <RecentRuns props={props} names={names} /> },
            ]
          : []
      }
    />
  );
}
